import { Injectable } from '@angular/core';
import { folderColors } from '../dictionaries/folderColors';
import { FirebaseUtilitiesService } from './firebase-utilities.service';
import { UploadHelperService } from './upload-helper.service';

const INDEX_FOLDER_NAME = 'all';
const indexFolderColor = '#eadbff';
const indexFolderLabel = 'INDEX';

/**
 * Service for handling folder-related business logic in ClientProfileComponent
 * Extracted to reduce component complexity and improve maintainability
 */
@Injectable({
  providedIn: 'root'
})
export class ClientProfileFolderService {
  constructor(
    private firebaseUtilitiesService: FirebaseUtilitiesService,
    private uploadHelperService: UploadHelperService
  ) {}

  /**
   * Creates a folder object for creation
   * @param folderName - Name of the folder
   * @param casename - Case name
   * @param userOwnerID - User owner ID
   * @param parentFolder - Parent folder (optional)
   * @returns Folder object ready for creation
   */
  createFolderObject(
    folderName: string,
    casename: string,
    userOwnerID: string,
    parentFolder?: any
  ): any {
    return {
      children: '',
      name: folderName.trim(),
      color: folderColors.custom,
      parentFolder: parentFolder?.id || '',
      parentId: parentFolder?.id ?? null,
      ownerID: userOwnerID,
      type: 'folder',
      belongsTo: casename,
      predefined: false,
      forDelete: false,
      folderId: this.uploadHelperService.getRandomString(20)
    };
  }

  /**
   * Checks if a folder name is duplicated in the same parent
   * @param folderName - Name to check
   * @param parentFolder - Parent folder
   * @param caseFolders - Array of existing case folders
   * @returns True if folder name is duplicated
   */
  checkDuplicatedFolder(folderName: string, parentFolder: any, caseFolders: any[]): boolean {
    const parentId = parentFolder ? parentFolder.id : null;
    return Boolean(
      caseFolders.find(
        folder =>
          folder.name.toLowerCase() === folderName.toLowerCase() &&
          (parentFolder ? folder.parentId === parentId : !folder.parentId)
      )
    );
  }

  /**
   * Gets folder name by folder ID
   * @param caseFolders - Array of case folders
   * @param parentFolderId - Parent folder ID
   * @returns Folder name or empty string
   */
  getFolderName(caseFolders: any[], parentFolderId: string): string {
    if ([INDEX_FOLDER_NAME, ''].includes(parentFolderId)) {
      return '';
    } else {
      const filtered = caseFolders.find(folder => folder.folderId === parentFolderId);
      return filtered ? filtered.name : '';
    }
  }

  /**
   * Gets folder ID by folder name
   * @param folderName - Name of the folder
   * @param caseFolders - Array of case folders
   * @returns Folder ID or undefined
   */
  getFolderIdByFolderName(folderName: string, caseFolders: any[]): string | undefined {
    if (!caseFolders || caseFolders.length === 0) return undefined;

    const folder = caseFolders.find(f => f.name === folderName);
    return folder ? folder.folderId : undefined;
  }

  /**
   * Creates an INDEX folder object
   * @param indexCount - Count of files in INDEX folder
   * @returns INDEX folder object
   */
  createIndexFolder(indexCount: number): any {
    const indexFolderNameCounter = `${indexFolderLabel} (${indexCount})`;
    return {
      type: 'folder',
      name: indexFolderNameCounter,
      folderId: INDEX_FOLDER_NAME,
      color: indexFolderColor,
      predefined: true
    };
  }

  /**
   * Gets folder extra metadata with counter
   * @param folder - Folder object
   * @param roleConsultant - Whether user is consultant
   * @param useremail - User email
   * @param filesShared - Shared files
   * @param uid - User ID
   * @returns Folder extra metadata
   */
  async getFolderExtra(
    folder: any,
    roleConsultant: boolean,
    useremail: string,
    filesShared: any[],
    uid: string
  ): Promise<any> {
    return {
      creator: uid,
      fileDesc: '',
      fileId: '', // Will be set by component
      fileName: folder.name,
      ftype: 'Folder',
      filePath: '',
      name: `${folder.name} ${await this.firebaseUtilitiesService.getNumberOfFilesByFolderId(
        folder.folderId,
        roleConsultant ? 'Consultant' : undefined,
        useremail,
        filesShared
      )}`,
      parentFolderName: folder.name,
      notes: [],
      sharedUsers: [],
      storename: '',
      viewerurl: ''
    };
  }

  /**
   * Gets folder name with counter
   * @param folder - Folder object
   * @param roleConsultant - Whether user is consultant
   * @param useremail - User email
   * @param filesShared - Shared files
   * @returns Folder name with file count
   */
  async getFolderNameWithCounter(
    folder: any,
    roleConsultant: boolean,
    useremail: string,
    filesShared: any[]
  ): Promise<string> {
    const result = await this.firebaseUtilitiesService.getNumberOfFilesByFolderId(
      folder.folderId,
      roleConsultant ? 'Consultant' : undefined,
      useremail,
      filesShared
    );
    return result;
  }

  /**
   * Gets folder name extra (with counter if not INDEX)
   * @param folder - Folder object
   * @param roleConsultant - Whether user is consultant
   * @param useremail - User email
   * @param filesShared - Shared files
   * @returns Folder name with or without counter
   */
  async getFolderNameExtra(
    folder: { name: string },
    roleConsultant: boolean,
    useremail: string,
    filesShared: any[]
  ): Promise<string> {
    if (folder.name && folder.name.substr(0, 5) === indexFolderLabel) {
      return `${folder.name}`;
    }
    return `${folder.name} ${await this.getFolderNameWithCounter(folder, roleConsultant, useremail, filesShared)}`;
  }

  /**
   * Builds hierarchical folder structure from flat array
   * @param folders - Flat array of folders
   * @returns Hierarchical folder structure
   */
  buildHierarchicalFolders(folders: any[]): any[] {
    const folderMap = new Map<string, any>();
    const rootFolders: any[] = [];

    // Create a map of all folders
    folders.forEach(folder => {
      folderMap.set(folder.id || folder.folderId, {
        ...folder,
        children: []
      });
    });

    // Build hierarchy
    folders.forEach(folder => {
      const folderId = folder.id || folder.folderId;
      const mappedFolder = folderMap.get(folderId);

      if (folder.parentId || folder.parentFolder) {
        const parentId = folder.parentId || folder.parentFolder;
        const parent = folderMap.get(parentId);
        if (parent) {
          parent.children.push(mappedFolder);
        } else {
          // Parent not found, treat as root
          rootFolders.push(mappedFolder);
        }
      } else {
        // No parent, it's a root folder
        rootFolders.push(mappedFolder);
      }
    });

    return rootFolders;
  }
}
